import React, { useEffect, useRef, useState } from 'react';
import { collection, query, where, doc, updateDoc, documentId, onSnapshot, getDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { User, Squad } from '../../../types';
import AttendanceGrids from './AttendanceGrids';
import NextClassSection from './NextClassSection';
import LevelCarousel from './LevelCarousel';
import LessonPlanModal from './LessonPlanModal';
import AttendanceModal from './AttendanceModal';
import TodaysSquadModal from './TodaysSquadModal';
import { getCurriculumAdapterById } from '../../curriculum/adapters/registry';
import { gameAPI, SavedSong as FirebaseSavedSong } from '../../games/services/GameAPI';
import { GAME_REGISTRY } from '../../games/models/GameRegistry';
import { getRarityColor } from '../../../shared/utils/rarity';
import RarityGem from '../../../shared/ui/RarityGem';
import { FirebaseService } from '../../../shared/storage/FirebaseService';
import { useAuth } from '../../auth/contexts/AuthContext';
import { DISMISSAL_META, getNextDismissalType, resolveStudentDismissal } from '../utils/dismissalProcedure';
import '../../muses/styles/MuseTiers.css';
import LoadingSpinner from '../../../shared/ui/LoadingSpinner';

interface DashboardPaneProps {
  squad: Squad;
  /** If true, auto-open attendance modal once on load (used for deep links) */
  initialOpenAttendance?: boolean;
  onAddStudent?: () => void;
}

interface SelectedLevelData {
  id: string;
  title: string;
  missions: string[];
}

interface StudentAchievement {
  id: string;
  title: string;
  description: string;
  iconUrl: string;
  museCashReward: number;
  rarity: string;
  gameId: string;
  unlockedAt: Date;
}

interface StudentGameStats {
  gameId: string;
  completions: number;
  bestScore: number;
  totalTimePlayed: number;
  achievements: StudentAchievement[];
  lastPlayed: Date;
  totalCashEarned?: number;
}

interface StudentSong {
  id: string;
  title: string;
  gameId: string;
  audioUrl?: string;
  createdAt: Date;
  duration?: number;
  bpm?: number;
  key?: string;
}

const DashboardPane: React.FC<DashboardPaneProps> = ({ squad, initialOpenAttendance, onAddStudent }) => {
  const { currentUser } = useAuth();
  const [students, setStudents] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState<(User & { achievements?: any[] }) | null>(null);
  const [isLessonPlanModalOpen, setIsLessonPlanModalOpen] = useState(false);
  const [isAttendanceModalOpen, setIsAttendanceModalOpen] = useState(false);
  const [isTodaysSquadModalOpen, setIsTodaysSquadModalOpen] = useState(false);
  const hasAutoOpenedAttendanceRef = useRef(false);
  const [isAwardingMuseCash, setIsAwardingMuseCash] = useState(false);
  const [selectedStudentsForAttendance, setSelectedStudentsForAttendance] = useState<User[]>([]);
  const [attendance, setAttendance] = useState<Record<string, boolean>>({});
  const [selectedLevelData, setSelectedLevelData] = useState<SelectedLevelData | null>(() => {
    if (!squad) return null;
    const adapter = getCurriculumAdapterById(squad.curriculumId || 'music');
    const completed = squad.completedLevels || [];
    const nums = completed.map(id => parseInt(id)).filter(n => !isNaN(n));
    const nextId = nums.length > 0
      ? String(Math.min(Math.max(Math.max(...nums) + 1, 1), adapter.levels.length))
      : '1';
    const levelFromData = adapter.getLevelById(nextId) as any;
    return {
      id: nextId,
      title: levelFromData?.title || `Level ${nextId}`,
      missions: levelFromData?.missions?.map((mission: any) => mission.text) || [
        'Every student gets 3 achievements in Rhythm Rally',
        'Students can clap along to a steady beat',
        'Class identifies quarter notes vs eighth notes'
      ]
    };
  });
  const [studentAchievements, setStudentAchievements] = useState<StudentAchievement[]>([]);
  const [studentGameStats, setStudentGameStats] = useState<StudentGameStats[]>([]);
  const [loadingStudentData, setLoadingStudentData] = useState(false);
  
  // Song-related state
  const [studentSongs, setStudentSongs] = useState<StudentSong[]>([]);
  const [loadingSongs, setLoadingSongs] = useState(false);
  const [currentlyPlayingSong, setCurrentlyPlayingSong] = useState<string | null>(null);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [studentCompletedLevels, setStudentCompletedLevels] = useState<{ id: string; title: string; submittedAt?: Date }[]>([]);
  const [showAllSongs, setShowAllSongs] = useState(false);
  const [topThreeSongs, setTopThreeSongs] = useState<FirebaseSavedSong[]>([]);
  const [parentConsents, setParentConsents] = useState<Record<string, boolean | null>>({});
  const [savingSelectedStudentDismissal, setSavingSelectedStudentDismissal] = useState(false);
  
  // Editable muse cash state
  const [isEditingMuseCash, setIsEditingMuseCash] = useState(false);
  const [editingMuseCash, setEditingMuseCash] = useState<number>(0);
  const [updatingMuseCash, setUpdatingMuseCash] = useState(false);
  const [museCashError, setMuseCashError] = useState<string | null>(null);
  
  // Song editing state
  const [editingSongId, setEditingSongId] = useState<string | null>(null);
  const [editingSongTitle, setEditingSongTitle] = useState<string>('');
  const [savingSongRename, setSavingSongRename] = useState(false);

  // Initialize or update selected level when squad changes
  useEffect(() => {
    if (!squad) return;

    // If a class is currently live, lock UI to the current lesson id
    if (squad.classStatus === 'current' && squad.currentLessonId) {
      const adapter = getCurriculumAdapterById(squad.curriculumId || 'music');
      const currentId = squad.currentLessonId;
      const levelFromData = adapter.getLevelById(currentId) as any;
      const liveLevelData: SelectedLevelData = {
        id: currentId,
        title: levelFromData?.title || `Level ${currentId}`,
        missions: levelFromData?.missions?.map((mission: any) => mission.text) || []
      };
      setSelectedLevelData(liveLevelData);
      return;
    }

    // Otherwise, determine default selected level as the first level marked "Next"
    const adapter = getCurriculumAdapterById(squad.curriculumId || 'music');
    const completed = squad.completedLevels || [];
    const nums = completed.map(id => parseInt(id)).filter(n => !isNaN(n));
    const nextId = nums.length > 0
      ? String(Math.min(Math.max(Math.max(...nums) + 1, 1), adapter.levels.length))
      : '1';
    const levelFromData = adapter.getLevelById(nextId) as any;

    const currentLevelData: SelectedLevelData = {
      id: nextId,
      title: levelFromData?.title || `Level ${nextId}`,
      missions: levelFromData?.missions?.map((mission: any) => mission.text) || [
        'Every student gets 3 achievements in Rhythm Rally',
        'Students can clap along to a steady beat', 
        'Class identifies quarter notes vs eighth notes'
      ]
    };
    setSelectedLevelData(currentLevelData);
  }, [squad]);

  useEffect(() => {
    if (students.length === 0) {
      setParentConsents({});
      return;
    }
    let cancelled = false;
    const run = async () => {
      const consents: Record<string, boolean | null> = {};
      for (const student of students) {
        try {
          if (student.parentIds && student.parentIds.length > 0) {
            const parentRef = doc(db, 'users', student.parentIds[0]);
            const parentSnap = await getDoc(parentRef);
            if (parentSnap.exists()) {
              const parentData = parentSnap.data() as User;
              const photoRelease = parentData.consents?.photoRelease;
              const photoConsent = photoRelease?.accepted === true || photoRelease?.value === 'accept';
              const hasResponse = photoRelease?.accepted !== undefined || photoRelease?.value !== undefined;
              consents[student.id] = hasResponse ? photoConsent : null;
            } else {
              consents[student.id] = null;
            }
          } else {
            consents[student.id] = null;
          }
        } catch (error) {
          console.error(`Error loading parent consent for student ${student.id}:`, error);
          consents[student.id] = null;
        }
      }
      if (!cancelled) setParentConsents(consents);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [students]);

  useEffect(() => {
    if (!selectedStudent) return;
    const refreshed = students.find((s) => s.id === selectedStudent.id);
    if (!refreshed) return;
    setSelectedStudent((prev) => (prev ? ({ ...prev, ...refreshed } as any) : prev));
  }, [students, selectedStudent?.id]);

  // Deep-link helper: open attendance modal once if requested
  useEffect(() => {
    if (!initialOpenAttendance) return;
    if (hasAutoOpenedAttendanceRef.current) return;
    hasAutoOpenedAttendanceRef.current = true;
    setIsAttendanceModalOpen(true);
  }, [initialOpenAttendance]);

  useEffect(() => {
    if (!squad.studentIds || squad.studentIds.length === 0) {
      setStudents([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    const studentsRef = collection(db, 'users');
    const studentsQuery = query(studentsRef, where(documentId(), 'in', squad.studentIds));

    const unsubscribe = onSnapshot(studentsQuery, (studentsSnapshot) => {
      const studentsData = studentsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as User));
      setStudents(studentsData);

      const initialAttendance: Record<string, boolean> = {};
      studentsData.forEach(student => {
        initialAttendance[student.id] = squad.attendance?.[student.id] ?? false;
      });
      setAttendance(initialAttendance);

      setLoading(false);
    }, (error) => {
      console.error("Error fetching students with snapshot: ", error);
      setLoading(false);
    });

    // Cleanup listener on component unmount
    return () => unsubscribe();
    
  }, [squad]);

  const handleLevelSelect = (levelData: SelectedLevelData) => {
    console.log('Level selected:', levelData);
    setSelectedLevelData(levelData);
  };

  const loadStudentData = async (student: User) => {
    setLoadingStudentData(true);
    setLoadingSongs(true);
    try {
      // Load user achievements
      const userAchievements = await gameAPI.getUserAchievements(student.id);
      const mappedAchievements: StudentAchievement[] = userAchievements.map(ach => ({
        id: ach.id || ach.achievementId || '',
        title: ach.title || 'Unknown Achievement',
        description: ach.description || '',
        iconUrl: ach.iconUrl || '🏆',
        museCashReward: ach.museCashReward || 0,
        rarity: ach.rarity || 'Common',
        gameId: ach.gameId || '',
        unlockedAt: ach.unlockedAt || new Date()
      }));
      setStudentAchievements(mappedAchievements);

      // Load game stats for each game
      const statsPromises = Object.keys(GAME_REGISTRY).map(async (gameId) => {
        try {
          const stats = await gameAPI.getUserGameStats(student.id, gameId);
          if (stats) {
            const gameAchievements = mappedAchievements.filter(ach => ach.gameId === gameId);
            return {
              ...stats,
              gameId,
              achievements: gameAchievements
            };
          }
        } catch (error) {
          console.log(`No stats found for game: ${gameId}`);
        }
        return null;
      });

      const allStats = await Promise.all(statsPromises);
      setStudentGameStats(allStats.filter(stat => stat !== null) as StudentGameStats[]);

      // Load student songs
      console.log(`🎵 Loading songs for student: ${student.displayName} (${student.id})`);
      const userSongs = await gameAPI.getUserSongs(student.id);
      const mappedSongs: StudentSong[] = userSongs.map(song => ({
        id: song.id || '',
        title: song.title || 'Untitled Song',
        gameId: song.gameId || '',
        audioUrl: song.audioUrl,
        createdAt: song.createdAt || new Date(),
        duration: song.duration,
        bpm: song.bpm,
        key: song.key
      }));
      setStudentSongs(mappedSongs);
      console.log(`📊 Found ${mappedSongs.length} songs for student`);

      // Load Top 3 from the student's saved Top 10 chart
      try {
        const { db } = await import('../../../shared/storage/firebase');
        const { doc, getDoc } = await import('firebase/firestore');
        const chartRef = doc(db, 'users', student.id, 'topCharts', 'top10');
        const chartSnap = await getDoc(chartRef);
        if (chartSnap.exists()) {
          const data = chartSnap.data() as any;
          const items = (data.items || []) as Array<{ songId: string; rank: number }>;
          const top3Ids = items
            .slice()
            .sort((a, b) => a.rank - b.rank)
            .slice(0, 3)
            .map(i => i.songId);
          const top3 = userSongs.filter(s => top3Ids.includes(s.id!));
          // Ensure order by rank
          top3.sort((a, b) => top3Ids.indexOf(a.id!) - top3Ids.indexOf(b.id!));
          setTopThreeSongs(top3);
        } else {
          setTopThreeSongs([]);
        }
      } catch (e) {
        console.warn('⚠️ Failed to load Top 3 songs for student', e);
        setTopThreeSongs([]);
      }

      // Load completed curriculum levels from reflection game state
      const reflectionStateRef = doc(db, 'users', student.id, 'reflectionGame', 'state');
      const reflectionSnap = await getDoc(reflectionStateRef);
      const completedLevelsState = reflectionSnap.exists() ? (reflectionSnap.data() as any).completedLevels || {} : {};
      const completedLevelIds = Object.keys(completedLevelsState);
      const adapter = getCurriculumAdapterById(squad.curriculumId || 'music');
      const mappedCompleted = completedLevelIds
        .sort((a, b) => parseInt(b) - parseInt(a))
        .map((levelId: string) => ({
          id: levelId,
          title: (adapter.getLevelById(levelId) as any)?.title || `Level ${levelId}`,
          submittedAt: completedLevelsState[levelId]?.submittedAt?.toDate?.() || undefined
        }));
      setStudentCompletedLevels(mappedCompleted);

    } catch (error) {
      console.error('Error loading student data:', error);
    }
    setLoadingStudentData(false);
    setLoadingSongs(false);
  };

  const handleStudentClick = async (student: User & { achievements?: any[] }) => {
    setSelectedStudent(student);
    setShowAllSongs(false);
    await loadStudentData(student);
  };

  const handleStudentPatch = (studentId: string, patch: Partial<User>) => {
    setStudents((prev) => prev.map((student) => (student.id === studentId ? ({ ...student, ...patch } as User) : student)));
    setSelectedStudent((prev) => (prev && prev.id === studentId ? ({ ...prev, ...patch } as any) : prev));
  };

  const handleModalDismissalToggle = async () => {
    if (!selectedStudent) return;
    if (savingSelectedStudentDismissal) return;

    const studentId = String(selectedStudent.id || '').trim();
    if (!studentId) return;

    const resolved = resolveStudentDismissal(selectedStudent as User);
    const nextType = getNextDismissalType(resolved.type);

    setSavingSelectedStudentDismissal(true);
    try {
      const patch: Partial<User> = {
        dismissalProcedureType: nextType === 'unassigned' ? null : nextType,
        dismissalProcedureNote: nextType === 'special'
          ? (resolved.otherText || (selectedStudent as any)?.dismissalProcedureNote || null)
          : null,
      };
      await updateDoc(doc(db, 'users', studentId), patch as any);
      handleStudentPatch(studentId, patch);
    } catch (error) {
      console.error(`Error updating dismissal procedure for student ${studentId}:`, error);
    } finally {
      setSavingSelectedStudentDismissal(false);
    }
  };

  const handleCloseStudentModal = () => {
    // Stop any playing audio when closing the modal
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }
    setCurrentlyPlayingSong(null);
    
    // Reset muse cash editing state
    setIsEditingMuseCash(false);
    setEditingMuseCash(0);
    setMuseCashError(null);
    
    setSelectedStudent(null);
    setShowAllSongs(false);
  };

  // Song playback functionality
  const playStudentSong = async (song: StudentSong) => {
    try {
      // Stop any currently playing audio
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        setCurrentAudio(null);
      }

      if (!song.audioUrl) {
        console.warn('No audio URL found for song:', song.title);
        // Could implement audio synthesis fallback here if needed
        return;
      }

      setCurrentlyPlayingSong(song.id);
      console.log('🎵 Playing student song:', song.title);

      const audio = new Audio(song.audioUrl);
      setCurrentAudio(audio);

      audio.onended = () => {
        setCurrentlyPlayingSong(null);
        setCurrentAudio(null);
      };

      audio.onerror = (error) => {
        console.error('❌ Error playing student song:', error);
        setCurrentlyPlayingSong(null);
        setCurrentAudio(null);
      };

      await audio.play();
    } catch (error) {
      console.error('❌ Error playing student song:', error);
      setCurrentlyPlayingSong(null);
      setCurrentAudio(null);
    }
  };

  const stopCurrentSong = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    setCurrentlyPlayingSong(null);
  };

  const getGameDataForSong = (song: StudentSong) => {
    // Normalize gameId to handle variations
    let resolvedGameId = song.gameId;
    
    // Handle PianoClicker variations
    if (resolvedGameId && /^pianoclicker/i.test(resolvedGameId)) {
      resolvedGameId = 'piano-clicker';
    }
    
    // Handle Harmonidome variations
    if (resolvedGameId && /^harmonidome$/i.test(resolvedGameId)) {
      resolvedGameId = 'harmonidome';
    }
    
    let game = GAME_REGISTRY[resolvedGameId];
    
    // Fallback: If game not found or is unknown, try to detect from song title
    if (!game || resolvedGameId === 'unknown') {
      const title = song.title?.toLowerCase() || '';
      
      // Check for game names in the title
      if (/piano\s*clicker/i.test(title)) {
        game = GAME_REGISTRY['piano-clicker'];
      } else if (/harmonidome/i.test(title)) {
        game = GAME_REGISTRY['harmonidome'];
      } else if (/beat\s*decoder/i.test(title)) {
        game = GAME_REGISTRY['beat-decoders'];
      } else if (/rhythm\s*rally/i.test(title)) {
        game = GAME_REGISTRY['rhythm-rally'];
      } else if (/melopede/i.test(title)) {
        game = GAME_REGISTRY['melopedes'];
      }
    }
    
    return {
      title: game?.title || 'Unknown Game',
      thumbnail: game?.thumbnail || '/default-game.png'
    };
  };

  const formatSongDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const downloadSong = async (song: StudentSong) => {
    if (!song.audioUrl) {
      console.warn('No audio URL available for download');
      return;
    }

    try {
      console.log('📥 Downloading song:', song.title);
      
      // Fetch the audio file
      const response = await fetch(song.audioUrl);
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename: sanitize song title and add .wav extension
      const sanitizedTitle = song.title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      link.download = `${sanitizedTitle}_${song.id}.wav`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Song downloaded successfully');
    } catch (error) {
      console.error('❌ Error downloading song:', error);
      void window.appAlert('Failed to download song. Please try again.');
    }
  };

  const handleRenameSong = async (songId: string, newTitle: string) => {
    if (!newTitle.trim() || savingSongRename || !selectedStudent) return;

    try {
      setSavingSongRename(true);
      console.log(`✏️ Renaming song ${songId} to "${newTitle}" for student ${selectedStudent.id}`);
      
      // Update the song in the student's collection (not the teacher's)
      const { db } = await import('../../../shared/storage/firebase');
      const { doc, updateDoc } = await import('firebase/firestore');
      const songRef = doc(db, 'users', selectedStudent.id, 'songs', songId);
      
      await updateDoc(songRef, {
        title: newTitle.trim(),
        updatedAt: new Date(),
      });
      
      // Update local state
      setStudentSongs(prev => prev.map(song => 
        song.id === songId ? { ...song, title: newTitle.trim() } : song
      ));
      
      // Also update topThreeSongs if the song is in there
      setTopThreeSongs(prev => prev.map(song =>
        song.id === songId ? { ...song, title: newTitle.trim() } : song
      ));
      
      setEditingSongId(null);
      setEditingSongTitle('');
      console.log(`✅ Song renamed successfully`);
      
    } catch (error) {
      console.error('❌ Error renaming song:', error);
      void window.appAlert('Failed to rename song. Please try again.');
    } finally {
      setSavingSongRename(false);
    }
  };

  const handleDeleteSong = async (song: StudentSong) => {
    if (!selectedStudent?.id || !song.id) return;

    const confirmed = await window.appConfirm(`Delete "${song.title}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      if (song.audioUrl) {
        try {
          const { getStorage, ref, deleteObject } = await import('firebase/storage');
          const storage = getStorage();
          await deleteObject(ref(storage, song.audioUrl));
        } catch (storageError) {
          console.warn('⚠️ Could not delete song audio from storage:', storageError);
        }
      }

      const songRef = doc(db, 'users', selectedStudent.id, 'songs', song.id);
      await deleteDoc(songRef);

      // Keep student's Top 10 chart in sync if this song was ranked.
      const chartRef = doc(db, 'users', selectedStudent.id, 'topCharts', 'top10');
      const chartSnap = await getDoc(chartRef);
      if (chartSnap.exists()) {
        const data = chartSnap.data() as any;
        const items = Array.isArray(data.items) ? data.items : [];
        const nextItems = items
          .filter((item: any) => String(item?.songId || '') !== song.id)
          .sort((a: any, b: any) => Number(a?.rank || 0) - Number(b?.rank || 0))
          .map((item: any, index: number) => ({
            songId: String(item.songId),
            rank: index + 1,
          }));

        await setDoc(chartRef, { items: nextItems, updatedAt: new Date() }, { merge: true });
      }

      setStudentSongs(prev => prev.filter(s => s.id !== song.id));
      setTopThreeSongs(prev => prev.filter(s => s.id !== song.id));

      if (currentlyPlayingSong === song.id) {
        stopCurrentSong();
      }

      if (editingSongId === song.id) {
        cancelEditingSong();
      }
    } catch (error) {
      console.error('❌ Error deleting student song:', error);
      void window.appAlert('Failed to delete song. Please try again.');
    }
  };

  const startEditingSong = (songId: string, currentTitle: string) => {
    setEditingSongId(songId);
    setEditingSongTitle(currentTitle);
  };

  const cancelEditingSong = () => {
    setEditingSongId(null);
    setEditingSongTitle('');
  };

  const handleSongKeyPress = (e: React.KeyboardEvent, songId: string) => {
    if (e.key === 'Enter') {
      handleRenameSong(songId, editingSongTitle);
    } else if (e.key === 'Escape') {
      cancelEditingSong();
    }
  };

  // Cleanup audio on component unmount
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        setCurrentAudio(null);
      }
    };
  }, [currentAudio]);

  const handleTakeAttendance = () => {
    setIsAttendanceModalOpen(true);
  };

  const handleAttendanceConfirmed = (selectedStudentData: { id: string; name: string; avatar: string }[]) => {
    // Convert back to User objects
    const selectedUsers = students.filter(student => 
      selectedStudentData.some(selected => selected.id === student.id)
    );
    setSelectedStudentsForAttendance(selectedUsers);

    // Update attendance based on selected students
    const newAttendance: Record<string, boolean> = {};
    students.forEach(student => {
      newAttendance[student.id] = selectedUsers.some(s => s.id === student.id);
    });
    
    setAttendance(newAttendance);
    saveAttendanceToFirebase(newAttendance);

    setIsAttendanceModalOpen(false);
    setIsTodaysSquadModalOpen(true);
  };

  const handleTodaysSquadConfirmed = async () => {
    setIsAwardingMuseCash(true);

    try {
      // Award 100 musecash to each student in attendance
      const attendanceRewards = selectedStudentsForAttendance.map(async (student) => {
        try {
          // Get current user data to add to existing musecash
          const userDoc = await getDoc(doc(db, 'users', student.id));
          const userData = userDoc.data();
          const currentMuseCash = userData?.museCash || 0;
          const newMuseCashAmount = currentMuseCash + 100;

          await FirebaseService.updateMuseCash(student.id, newMuseCashAmount);
          console.log(`✅ Awarded 100 musecash to ${student.displayName} (${currentMuseCash} → ${newMuseCashAmount})`);
          return { success: true, student: student.displayName };
        } catch (error) {
          console.error(`❌ Failed to award musecash to ${student.displayName}:`, error);
          return { success: false, student: student.displayName, error };
        }
      });

      // Wait for all rewards to be processed
      const results = await Promise.all(attendanceRewards);
      const successful = results.filter(r => r.success);
      const failed = results.filter(r => !r.success);

      if (successful.length > 0) {
        console.log(`🎉 Successfully awarded 100 musecash to ${successful.length} students in attendance!`);
      }

      if (failed.length > 0) {
        console.error(`⚠️ Failed to award musecash to ${failed.length} students:`, failed);
      }

    } catch (error) {
      console.error('❌ Error awarding attendance musecash:', error);
    } finally {
      setIsAwardingMuseCash(false);
      setIsTodaysSquadModalOpen(false);
    }
  };

  const saveAttendanceToFirebase = async (newAttendance: Record<string, boolean>) => {
    if (!currentUser) {
      console.error("Cannot save attendance without a logged-in teacher.");
      return;
    }

    const squadRef = doc(db, "squads", squad.id);
    await updateDoc(squadRef, {
      attendance: newAttendance,
      attendanceMetadata: {
        lastTaken: new Date().toISOString(),
        takenBy: currentUser.uid,
      },
      lastAttendanceUpdate: new Date(),
    });
    setAttendance(newAttendance);
  };

  // Muse cash editing functions
  const handleMuseCashClick = () => {
    if (selectedStudent) {
      setEditingMuseCash(selectedStudent.museCash || 0);
      setIsEditingMuseCash(true);
      setMuseCashError(null);
    }
  };

  const handleMuseCashCancel = () => {
    setIsEditingMuseCash(false);
    setEditingMuseCash(0);
    setMuseCashError(null);
  };

  const handleMuseCashSave = async () => {
    if (!selectedStudent || !currentUser) {
      setMuseCashError('Unable to update: missing student or teacher information');
      return;
    }

    // Validate input
    if (editingMuseCash < 0) {
      setMuseCashError('Muse cash cannot be negative');
      return;
    }

    if (!Number.isInteger(editingMuseCash)) {
      setMuseCashError('Muse cash must be a whole number');
      return;
    }

    setUpdatingMuseCash(true);
    setMuseCashError(null);

    try {
      console.log(`🏛️ Teacher ${currentUser.uid} updating student ${selectedStudent.id} muse cash to ${editingMuseCash}`);
      
      // Update the student's muse cash using FirebaseService
      await FirebaseService.updateMuseCash(selectedStudent.id, editingMuseCash);
      
      // Update local state to reflect the change immediately
      setSelectedStudent(prev => prev ? { ...prev, museCash: editingMuseCash } : null);
      
      // Also update the students array to keep it in sync
      setStudents(prev => prev.map(student => 
        student.id === selectedStudent.id 
          ? { ...student, museCash: editingMuseCash }
          : student
      ));

      setIsEditingMuseCash(false);
      console.log(`✅ Successfully updated muse cash for ${selectedStudent.displayName}`);
      
    } catch (error) {
      console.error('Error updating muse cash:', error);
      setMuseCashError('Failed to update muse cash. Please try again.');
    } finally {
      setUpdatingMuseCash(false);
    }
  };

  const handleMuseCashChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setEditingMuseCash(isNaN(value) ? 0 : value);
    setMuseCashError(null);
  };

  const handleMuseCashKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleMuseCashSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleMuseCashCancel();
    }
  };

  if (loading) {
    return <div>Loading dashboard data...</div>;
  }

  return (
    <div className="dashboard-pane">
      <div className="dashboard-main-content">
        <AttendanceGrids
          students={students}
          attendance={attendance}
          onStudentClick={handleStudentClick}
          onStudentPatched={handleStudentPatch}
          squadId={squad.id}
          isTeacher={true}
          onAddStudent={onAddStudent}
        />
      </div>

      <div className="dashboard-sidebar">
        {squad.classStatus !== 'current' && (
          <LevelCarousel 
            squad={squad}
            onLevelSelect={handleLevelSelect}
          />
        )}
        <NextClassSection 
          squad={squad}
          selectedLevelId={
            squad.classStatus === 'current'
              ? (squad.currentLessonId || selectedLevelData?.id || '1')
              : (selectedLevelData?.id || squad.currentLessonId || '1')
          }
          onTakeAttendance={handleTakeAttendance}
          onSquadUpdate={() => {/* TODO: Add squad update handler */}}
        />
      </div>

      {/* Student Stats Modal */}
      {selectedStudent && (
        <div className="student-stats-modal-overlay" onClick={handleCloseStudentModal}>
          <div className="student-stats-modal-enhanced" onClick={e => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={handleCloseStudentModal}>&times;</button>
            
            {/* Header Section */}
            <div className="student-header-section">
              <div className="student-avatar-and-cash">
                <div className="student-avatar-large">
                  {selectedStudent.selectedMuse && selectedStudent.selectedMuse.img && (
                    <img
                      src={selectedStudent.selectedMuse.img}
                      alt={selectedStudent.selectedMuse.name}
                      className={`student-muse-avatar ${selectedStudent.selectedMuse.tier ? `muse-tier-${selectedStudent.selectedMuse.tier}` : ''}`}
                    />
                  )}
                </div>
                <div className="student-cash-display">
                  {isEditingMuseCash ? (
                    <div className="cash-edit-mode">
                      <input
                        type="number"
                        value={editingMuseCash}
                        onChange={handleMuseCashChange}
                        onKeyDown={handleMuseCashKeyDown}
                        className="cash-edit-input"
                        min="0"
                        step="1"
                        disabled={updatingMuseCash}
                        autoFocus
                      />
                      <div className="cash-edit-buttons">
                        <button
                          onClick={handleMuseCashSave}
                          disabled={updatingMuseCash}
                          className="cash-save-btn"
                          title="Save changes"
                        >
                          {updatingMuseCash ? '⏳' : '✓'}
                        </button>
                        <button
                          onClick={handleMuseCashCancel}
                          disabled={updatingMuseCash}
                          className="cash-cancel-btn"
                          title="Cancel editing"
                        >
                          ✕
                        </button>
                      </div>
                      {museCashError && (
                        <div className="cash-error">{museCashError}</div>
                      )}
                    </div>
                  ) : (
                    <div className="cash-display-mode" onClick={handleMuseCashClick}>
                      <div className="cash-amount" title="Click to edit muse cash">
                        {selectedStudent.museCash || 0}
                        <span className="cash-edit-icon">✏️</span>
                      </div>
                      <div className="cash-label">Muse Cash</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="student-basic-info">
                <h1 className="student-name">{selectedStudent.displayName}</h1>
                <div className="student-muse-info">
                  <span className="muse-name">{selectedStudent.selectedMuse?.name || 'No Muse Selected'}</span>
                  {selectedStudent.selectedMuse?.rarity && (
                    <span className="muse-rarity-enhanced">
                      {selectedStudent.selectedMuse.rarity}
                    </span>
                  )}
                </div>
                <div className="student-status-icons">
                  {(() => {
                    const hasParent = !!(selectedStudent.parentIds && selectedStudent.parentIds.length > 0);
                    return (
                      <div
                        className={`student-status-icon parent ${hasParent ? 'linked' : 'unlinked'}`}
                        title={hasParent ? 'Parent account linked' : 'No linked parent account'}
                      >
                        🏠<span className={hasParent ? 'consent-check' : 'consent-x'}>{hasParent ? '✓' : '✕'}</span>
                      </div>
                    );
                  })()}
                  {(() => {
                    const photoConsent = parentConsents[selectedStudent.id];
                    if (photoConsent === true) {
                      return <div className="student-status-icon photo" title="Photo consent on file">📷</div>;
                    }
                    return null;
                  })()}
                  {(() => {
                    const resolvedDismissal = resolveStudentDismissal(selectedStudent as User);
                    const iconMeta = DISMISSAL_META[resolvedDismissal.type];
                    return (
                      <button
                        type="button"
                        className={`student-status-icon dismissal ${savingSelectedStudentDismissal ? 'saving' : ''}`}
                        onClick={() => { void handleModalDismissalToggle(); }}
                        title={`${resolvedDismissal.tooltip} Click to change.`}
                        aria-label={`Dismissal procedure: ${iconMeta.shortLabel}. Click to change.`}
                        disabled={savingSelectedStudentDismissal}
                      >
                        {iconMeta.emoji}
                      </button>
                    );
                  })()}
                </div>
              </div>
            </div>

            {loadingStudentData ? (
              <div className="loading-section">
                <LoadingSpinner label="Loading student data…" />
              </div>
            ) : (
              <>
              {/* Top 3 Songs Section */}
              {topThreeSongs.length > 0 && (
                <div className="top-three-songs-section">
                  <h3>🏆 Top 3 songs</h3>
                  <div className="student-songs-list">
                    {topThreeSongs.map((song) => {
                      const gameData = getGameDataForSong({
                        id: song.id || '',
                        title: song.title || 'Untitled Song',
                        gameId: song.gameId || '',
                        audioUrl: song.audioUrl,
                        createdAt: song.createdAt || new Date(),
                        duration: song.duration,
                        bpm: song.bpm,
                        key: song.key
                      });
                      const isPlaying = currentlyPlayingSong === song.id;
                      return (
                        <div key={`top3-${song.id}`} className="student-song-card">
                          <div className="song-image-container">
                            <img 
                              src={gameData.thumbnail} 
                              alt={gameData.title}
                              className="song-thumbnail"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/default-game.png';
                              }}
                            />
                            <button 
                              className={`song-play-button ${isPlaying ? 'playing' : ''}`}
                              onClick={() => isPlaying ? stopCurrentSong() : playStudentSong({
                                id: song.id || '',
                                title: song.title || 'Untitled Song',
                                gameId: song.gameId || '',
                                audioUrl: song.audioUrl,
                                createdAt: song.createdAt || new Date(),
                                duration: song.duration,
                                bpm: song.bpm,
                                key: song.key
                              })}
                              disabled={!song.audioUrl}
                              title={song.audioUrl ? (isPlaying ? 'Stop song' : 'Play song') : 'No audio available'}
                            >
                              {isPlaying ? (
                                <div className="playing-animation">⏹</div>
                              ) : (
                                <div className="play-triangle">▶</div>
                              )}
                            </button>
                          </div>
                          <div className="song-info">
                            {editingSongId === song.id ? (
                              <input
                                type="text"
                                value={editingSongTitle}
                                onChange={(e) => setEditingSongTitle(e.target.value)}
                                onKeyDown={(e) => handleSongKeyPress(e, song.id!)}
                                onBlur={() => handleRenameSong(song.id!, editingSongTitle)}
                                className="song-title-input"
                                autoFocus
                                disabled={savingSongRename}
                              />
                            ) : (
                              <div 
                                className="song-title editable-title" 
                                onClick={() => startEditingSong(song.id!, song.title)}
                                title="Click to edit song name"
                              >
                                {song.title}
                              </div>
                            )}
                            <div className="song-game">from {gameData.title}</div>
                            <div className="song-date">{(song.createdAt || new Date()).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                            {!song.audioUrl && (
                              <div className="no-audio-badge">No audio</div>
                            )}
                          </div>
                          <div className="song-actions">
                            {song.audioUrl && (
                              <button
                                className="song-download-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadSong({
                                    id: song.id || '',
                                    title: song.title || 'Untitled Song',
                                    gameId: song.gameId || '',
                                    audioUrl: song.audioUrl,
                                    createdAt: song.createdAt || new Date(),
                                    duration: song.duration,
                                    bpm: song.bpm,
                                    key: song.key
                                  });
                                }}
                                title="Download song"
                              >
                                ⬇️
                              </button>
                            )}
                            <button
                              className="song-delete-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleDeleteSong({
                                  id: song.id || '',
                                  title: song.title || 'Untitled Song',
                                  gameId: song.gameId || '',
                                  audioUrl: song.audioUrl,
                                  createdAt: song.createdAt || new Date(),
                                  duration: song.duration,
                                  bpm: song.bpm,
                                  key: song.key
                                });
                              }}
                              title="Delete song"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
                {/* Student Songs Section - At the top */}
                <div className="student-songs-section">
                  <h3>🎵 Songs Created</h3>
                  {loadingSongs ? (
                    <div className="songs-loading">
                      <p>Loading songs...</p>
                    </div>
                  ) : studentSongs.length > 0 ? (
                    <div className="student-songs-list">
                      {(showAllSongs ? studentSongs : studentSongs.slice(0, 6)).map((song) => {
                        const gameData = getGameDataForSong(song);
                        const isPlaying = currentlyPlayingSong === song.id;
                        
                        return (
                          <div key={song.id} className="student-song-card">
                            <div className="song-image-container">
                              <img 
                                src={gameData.thumbnail} 
                                alt={gameData.title}
                                className="song-thumbnail"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/default-game.png';
                                }}
                              />
                              <button 
                                className={`song-play-button ${isPlaying ? 'playing' : ''}`}
                                onClick={() => isPlaying ? stopCurrentSong() : playStudentSong(song)}
                                disabled={!song.audioUrl}
                                title={song.audioUrl ? (isPlaying ? 'Stop song' : 'Play song') : 'No audio available'}
                              >
                                {isPlaying ? (
                                  <div className="playing-animation">⏹</div>
                                ) : (
                                  <div className="play-triangle">▶</div>
                                )}
                              </button>
                            </div>
                            <div className="song-info">
                              {editingSongId === song.id ? (
                                <input
                                  type="text"
                                  value={editingSongTitle}
                                  onChange={(e) => setEditingSongTitle(e.target.value)}
                                  onKeyDown={(e) => handleSongKeyPress(e, song.id!)}
                                  onBlur={() => handleRenameSong(song.id!, editingSongTitle)}
                                  className="song-title-input"
                                  autoFocus
                                  disabled={savingSongRename}
                                />
                              ) : (
                                <div 
                                  className="song-title editable-title" 
                                  onClick={() => startEditingSong(song.id!, song.title)}
                                  title="Click to edit song name"
                                >
                                  {song.title}
                                </div>
                              )}
                              <div className="song-game">from {gameData.title}</div>
                              <div className="song-date">{formatSongDate(song.createdAt)}</div>
                              {!song.audioUrl && (
                                <div className="no-audio-badge">No audio</div>
                              )}
                            </div>
                            <div className="song-actions">
                              {song.audioUrl && (
                                <button
                                  className="song-download-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadSong(song);
                                  }}
                                  title="Download song"
                                >
                                  ⬇️
                                </button>
                              )}
                              <button
                                className="song-delete-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleDeleteSong(song);
                                }}
                                title="Delete song"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {studentSongs.length > 6 && !showAllSongs && (
                        <div className="more-songs-indicator" onClick={() => setShowAllSongs(true)} style={{ cursor: 'pointer' }}>
                          +{studentSongs.length - 6} more song{studentSongs.length - 6 !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="no-songs-message">
                      <p>🎼 No songs created yet</p>
                      <p>Encourage {selectedStudent.displayName} to create music in composition games!</p>
                    </div>
                  )}
                </div>

                {/* Enhanced Stats Grid */}
                <div className="enhanced-stats-grid">
                  <div className="enhanced-stat-card achievements-card">
                    <div className="stat-background">
                      <div className="stat-icon-large">🏆</div>
                    </div>
                    <div className="stat-content">
                      <div className="stat-number-large">{studentAchievements.length}</div>
                      <div className="stat-label-enhanced">Achievements</div>
                      <div className="stat-subtitle">Unlocked</div>
                    </div>
                    <div className="stat-glow achievements-glow"></div>
                  </div>
                  
                  <div className="enhanced-stat-card cash-card">
                    <div className="stat-background">
                      <div className="stat-icon-large">💰</div>
                    </div>
                    <div className="stat-content">
                      <div className="stat-number-large">{studentAchievements.reduce((total, ach) => total + ach.museCashReward, 0)}</div>
                      <div className="stat-label-enhanced">Cash Earned</div>
                      <div className="stat-subtitle">From achievements</div>
                    </div>
                    <div className="stat-glow cash-glow"></div>
                  </div>
                  
                  <div className="enhanced-stat-card songs-card">
                    <div className="stat-background">
                      <div className="stat-icon-large">🎼</div>
                    </div>
                    <div className="stat-content">
                      <div className="stat-number-large">{studentSongs.length}</div>
                      <div className="stat-label-enhanced">Songs Created</div>
                      <div className="stat-subtitle">Original compositions</div>
                    </div>
                    <div className="stat-glow songs-glow"></div>
                  </div>
                </div>

                {/* Completed Levels */}
                <div className="completed-levels-section">
                  <h3>Completed Levels</h3>
                  {studentCompletedLevels.length === 0 ? (
                    <div className="no-completed-levels">No levels completed yet</div>
                  ) : (
                    <div className="completed-levels-scroll">
                      {studentCompletedLevels.map((l, idx) => (
                        <div key={l.id} className={`completed-level-card ${idx === 0 ? 'largest' : ''}`} title={l.title}>
                          <div className="cl-level-number">Level {l.id}</div>
                          <div className="cl-title">{l.title.replace(/^Level\s*\d+:\s*/, '')}</div>
                          {l.submittedAt && (
                            <div className="cl-date">{l.submittedAt.toLocaleDateString()}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Muse Collection */}
                {selectedStudent.muses && selectedStudent.muses.length > 0 && (
                  <div className="rarity-breakdown">
                    <h3>Muse Collection</h3>
                    <div className="rarity-grid">
                      {['Legendary', 'Epic', 'Rare', 'Uncommon', 'Common'].map(rarity => {
                        const rarityMuses = (selectedStudent.muses || []).filter(muse => muse.rarity === rarity);
                        return (
                          <div key={rarity} className="rarity-card">
                            <div className="rarity-header">
                              <RarityGem rarity={rarity} className="rarity-gem-large" />
                              <div className="rarity-content">
                                <div className="rarity-count">{rarityMuses.length}</div>
                                <div className="rarity-name">{rarity}</div>
                              </div>
                            </div>
                            <div className="muse-icons">
                              {rarityMuses.slice(0, 4).map((muse, i) => (
                                <div key={i} className="muse-preview" title={muse.name}>
                                  <img 
                                    src={muse.img} 
                                    alt={muse.name}
                                    className="muse-preview-img"
                                  />
                                </div>
                              ))}
                              {rarityMuses.length > 4 && (
                                <div className="muse-more">+{rarityMuses.length - 4}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Achievements by Game */}
                {studentAchievements.length > 0 && (
                  <div className="achievements-by-game">
                    <h3>Achievements by Game</h3>
                    <div className="game-achievement-grid">
                      {Object.keys(GAME_REGISTRY)
                        .map(gameId => {
                          const gameAchievements = studentAchievements.filter(ach => ach.gameId === gameId);
                          if (gameAchievements.length === 0) return null;
                          
                          const game = GAME_REGISTRY[gameId];
                          const rarityBreakdown = gameAchievements.reduce((acc, ach) => {
                            acc[ach.rarity] = (acc[ach.rarity] || 0) + 1;
                            return acc;
                          }, {} as Record<string, number>);
                          
                          return (
                            <div key={gameId} className="game-achievement-card">
                              <div className="game-achievement-header">
                                <div className="game-info">
                                  <div className="game-title-achievement">{game?.title || gameId}</div>
                                  <div className="achievement-count-badge">
                                    {gameAchievements.length} achievement{gameAchievements.length !== 1 ? 's' : ''}
                                  </div>
                                </div>
                                                                 <div className="total-cash-earned">
                                   +{gameAchievements.reduce((total, ach) => total + ach.museCashReward, 0)} 💰
                                 </div>
                              </div>
                              
                              <div className="game-rarity-breakdown">
                                {['Legendary', 'Epic', 'Rare', 'Uncommon', 'Common'].map(rarity => {
                                  const count = rarityBreakdown[rarity] || 0;
                                  if (count === 0) return null;
                                  return (
                                    <div key={rarity} className="rarity-pill" style={{ borderColor: getRarityColor(rarity) }}>
                                      <RarityGem rarity={rarity} className="rarity-gem-small" />
                                      <span className="rarity-count-small">{count}</span>
                                    </div>
                                  );
                                })}
                              </div>
                              
                              <div className="game-achievements-preview">
                                {gameAchievements
                                  .sort((a, b) => new Date(b.unlockedAt).getTime() - new Date(a.unlockedAt).getTime())
                                  .slice(0, 3)
                                  .map((achievement, i) => (
                                    <div key={i} className="achievement-preview" title={achievement.title}>
                                      <div className="achievement-icon-small">{achievement.iconUrl}</div>
                                      <div className="achievement-preview-info">
                                        <div className="achievement-preview-title">{achievement.title}</div>
                                        <div className="achievement-preview-rarity" style={{ color: getRarityColor(achievement.rarity) }}>
                                          {achievement.rarity}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                {gameAchievements.length > 3 && (
                                  <div className="more-achievements">
                                    +{gameAchievements.length - 3} more
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })
                        .filter(Boolean)}
                    </div>
                  </div>
                )}

                {/* Game Progress */}
                {studentGameStats.length > 0 && (
                  <div className="game-progress-section">
                    <h3>Game Progress</h3>
                    <div className="game-progress-grid">
                      {studentGameStats.map((stats) => {
                        const game = GAME_REGISTRY[stats.gameId];
                        if (!game) return null;

                        return (
                          <div key={stats.gameId} className="game-progress-card">
                            <div className="game-progress-header">
                              <div className="game-title">{game.title}</div>
                              <div className="game-achievements-count">
                                {stats.achievements.length} achievements
                              </div>
                            </div>
                            <div className="game-progress-stats">
                              <div className="progress-stat">
                                <span className="progress-label">Completions:</span>
                                <span className="progress-value">{stats.completions}</span>
                              </div>
                              <div className="progress-stat">
                                <span className="progress-label">Best Score:</span>
                                <span className="progress-value">{stats.bestScore}</span>
                              </div>
                              <div className="progress-stat">
                                <span className="progress-label">Time Played:</span>
                                <span className="progress-value">{Math.round(stats.totalTimePlayed / 60)}m</span>
                              </div>
                            </div>
                            {stats.achievements.length > 0 && (
                              <div className="game-achievements-preview">
                                {stats.achievements.slice(0, 4).map((ach, i) => (
                                  <span 
                                    key={i} 
                                    className="game-achievement-icon"
                                    title={ach.title}
                                    style={{ borderColor: getRarityColor(ach.rarity) }}
                                  >
                                    {ach.iconUrl}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {studentAchievements.length === 0 && !loadingStudentData && (
                  <div className="empty-achievements">
                    <div className="empty-icon">🌟</div>
                    <h3>Ready to Achieve Greatness!</h3>
                    <p>{selectedStudent.displayName} is just getting started on their musical journey.</p>
                    <p>Encourage them to play games and create music to earn their first achievements!</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Lesson Plan Modal */}
      <LessonPlanModal
        isOpen={isLessonPlanModalOpen}
        onClose={() => setIsLessonPlanModalOpen(false)}
      />

      {/* Attendance Modal */}
      <AttendanceModal
        isOpen={isAttendanceModalOpen}
        onClose={() => setIsAttendanceModalOpen(false)}
        students={students.map(student => ({
          id: student.id,
          name: student.displayName || 'Student',
          avatar: (student.selectedMuse && typeof student.selectedMuse === 'object' && student.selectedMuse.img) 
                  ? student.selectedMuse.img 
                  : '/default-avatar.png'
        }))}
        onConfirmAttendance={handleAttendanceConfirmed}
      />

      {/* Today's Squad Modal */}
      <TodaysSquadModal
        isOpen={isTodaysSquadModalOpen}
        onClose={() => setIsTodaysSquadModalOpen(false)}
        selectedStudents={selectedStudentsForAttendance.map(student => ({
          id: student.id,
          name: student.displayName || 'Student',
          avatar: (student.selectedMuse && typeof student.selectedMuse === 'object' && student.selectedMuse.img)
                  ? student.selectedMuse.img
                  : '/default-avatar.png'
        }))}
        onConfirm={handleTodaysSquadConfirmed}
        isAwarding={isAwardingMuseCash}
      />
    </div>
  );
};

export default DashboardPane;
